import { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faXmark } from "@fortawesome/pro-solid-svg-icons";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogClose } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { COOKIE_CATEGORIES, type CookieCategory } from "./cookieCategories";
import { getConsent } from "@/lib/consent/consent";
import { updateConsent } from "@/lib/consent/loader";
import { hasGpcSignal } from "@/lib/consent/region";
import { PILL_CYAN, PILL_GHOST_ON_LIGHT } from "./consentStyles";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function CookiePreferencesModal({ open, onOpenChange }: Props) {
  const [functional, setFunctional] = useState(true);
  const [advertising, setAdvertising] = useState(true);

  // Re-seed toggles from the stored choice each time the modal opens. With no
  // stored choice, Advertising seeds from GPC (denied under the signal) so
  // submitting untouched can't silently convert it into an explicit grant.
  useEffect(() => {
    if (!open) return;
    const c = getConsent();
    setFunctional(c ? c.functional : true);
    setAdvertising(c ? c.advertising : !hasGpcSignal());
  }, [open]);

  const commit = (fn: boolean, adv: boolean) => {
    updateConsent(fn, adv);
    onOpenChange(false);
  };

  const toggles: Record<CookieCategory["key"], { value: boolean; set: (v: boolean) => void } | null> = {
    required: null,
    functional: { value: functional, set: setFunctional },
    advertising: { value: advertising, set: setAdvertising },
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] w-[calc(100%-2rem)] max-w-lg overflow-y-auto overflow-x-hidden rounded-2xl border border-black/[0.08] bg-white text-zinc-900 ![animation:none] sm:rounded-2xl">
        {/* The global dialog close (dialog.tsx) is `hidden md:inline-flex`; add a mobile-only ✕ so small screens can dismiss. */}
        <DialogClose className="absolute right-3 top-3 z-10 inline-flex h-8 w-8 items-center justify-center rounded-full text-zinc-500 transition-colors hover:bg-black/5 hover:text-zinc-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan focus-visible:ring-offset-2 focus-visible:ring-offset-white md:hidden">
          <FontAwesomeIcon icon={faXmark} className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogClose>
        <DialogHeader className="text-left">
          <DialogTitle className="text-lg font-black uppercase italic tracking-[0.02em] text-black">
            Cookie Preferences
          </DialogTitle>
          <DialogDescription className="text-zinc-600">
            Choose which cookies and tracking technologies we may use. See our Privacy Policy for details.
          </DialogDescription>
        </DialogHeader>
        {COOKIE_CATEGORIES.map((cat) => {
          const toggle = toggles[cat.key];
          return (
            <section key={cat.key} className="border-b border-black/[0.09] pb-4 last:border-0">
              <div className="flex items-center justify-between gap-4">
                <h3 className="text-sm font-bold text-black">{cat.title}</h3>
                {toggle ? (
                  <Switch
                    checked={toggle.value}
                    onCheckedChange={toggle.set}
                    aria-label={cat.title}
                    className="data-[state=checked]:bg-cyan data-[state=unchecked]:bg-zinc-300 [&_span]:bg-white [&_span]:shadow focus-visible:ring-cyan focus-visible:ring-offset-white"
                  />
                ) : (
                  <span className="whitespace-nowrap rounded-full bg-cyan px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-wide text-black">
                    Always Active
                  </span>
                )}
              </div>
              <p className="mt-1 text-sm text-zinc-600">{cat.description}</p>
              <details className="group mt-2">
                <summary className="inline-flex cursor-pointer list-none items-center gap-1.5 rounded text-sm font-medium text-[hsl(190,71%,35%)] ring-offset-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan focus-visible:ring-offset-2 group-open:text-zinc-900 [&::-webkit-details-marker]:hidden">
                  <span className="group-open:hidden">
                    View {cat.title} ({cat.cookies.length})
                  </span>
                  <span className="hidden group-open:inline">Hide {cat.title}</span>
                  <FontAwesomeIcon
                    icon={faChevronDown}
                    className="h-2.5 w-2.5 transition-transform duration-200 group-open:rotate-180"
                  />
                </summary>
                <ul className="mt-2 space-y-1.5 rounded-[10px] border border-black/[0.07] bg-zinc-100 px-3 py-2.5 text-xs text-zinc-600">
                  {cat.cookies.map((c) => (
                    <li key={c.name}>
                      <span className="rounded bg-black/[0.08] px-1 font-mono text-zinc-900">{c.name}</span> —{" "}
                      {c.provider}, {c.lifetime}. {c.purpose}
                    </li>
                  ))}
                </ul>
              </details>
            </section>
          );
        })}
        <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
          <button type="button" className={`${PILL_GHOST_ON_LIGHT} px-4 py-3 text-[13px] ring-offset-white`} onClick={() => commit(false, false)}>
            Reject Optional
          </button>
          <button type="button" className={`${PILL_GHOST_ON_LIGHT} px-4 py-3 text-[13px] ring-offset-white`} onClick={() => commit(functional, advertising)}>
            Submit Preferences
          </button>
          <button type="button" className={`${PILL_CYAN} px-5 py-3 text-[13px] ring-offset-white`} onClick={() => commit(true, true)}>
            Accept All
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
